import { createApiKeyApi, deleteApiKeyApi } from '@/api-calls/api-keys/mutate'
import { getApiKeysApi } from '@/api-calls/api-keys/get'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@blogging/ui'
import { useSite } from '@/hooks/use-site'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { Check, Copy, Eye, EyeOff, Key, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface ApiKey {
  id: string
  name: string
  secret: string
  access: 'read' | 'write'
  lastSeenAt: string | null
  createdAt: string
}

export function SiteSettingsApiKeysPage() {
  const { siteId } = useParams()
  const { site } = useSite()
  const queryClient = useQueryClient()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [allowWrite, setAllowWrite] = useState(false)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null)
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null)
  const [visibleKeyId, setVisibleKeyId] = useState<string | null>(null)

  const {
    data: apiKeys,
    isLoading,
    error,
  } = useFetch(getApiKeysApi, {
    resourceName: 'apiKeys',
    initialParams: { siteId: siteId as string },
  })

  const { mutate: createApiKey, isPending: isCreating } = useMutation(
    createApiKeyApi,
    {
      resourceName: 'apiKey',
      onSuccess: (data: any) => {
        queryClient.invalidateQueries({
          queryKey: [getApiKeysApi.apiName, { siteId }],
        })
        setNewlyCreatedKey(data.apiKey.secret)
        setNewKeyName('')
        setAllowWrite(false)
        toast.success('API key created successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to create API key')
      },
    },
  )

  const { mutate: deleteApiKey, isPending: isDeleting } = useMutation(
    deleteApiKeyApi,
    {
      resourceName: 'apiKey',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getApiKeysApi.apiName, { siteId }],
        })
        setIsDeleteDialogOpen(false)
        setSelectedKeyId(null)
        toast.success('API key deleted successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to delete API key')
      },
    },
  )

  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key')
      return
    }
    createApiKey({
      siteId: siteId as string,
      name: newKeyName.trim(),
      access: allowWrite ? 'write' : 'read',
    })
  }

  const handleDeleteKey = () => {
    if (selectedKeyId) {
      deleteApiKey({ siteId: siteId as string, id: selectedKeyId })
    }
  }

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKeyId(keyId)
      setTimeout(() => setCopiedKeyId(null), 2000)
      toast.success('Copied to clipboard!')
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const maskSecret = (secret: string) => {
    if (secret.length <= 8) return '••••••••'
    return secret.slice(0, 4) + '••••••••' + secret.slice(-4)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-destructive">Error loading API keys</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="mt-2">
            Retry
          </Button>
        </div>
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">
            Manage API keys for accessing your site's content via the Content API
            or letting AI agents collaborate on posts and pages over MCP.
          </p>
        </div>

        {/* Info Card */}
        <div className="rounded-lg border bg-blue-50 dark:bg-blue-950 p-4">
          <div className="flex items-start gap-3">
            <Key className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Content API Access
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Use these API keys to authenticate requests to the Content API.
                Include the key in the <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">Authorization</code> header
                as <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">Bearer YOUR_API_KEY</code>,
                along with your Site ID to identify the site.
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Want an AI assistant to draft and edit posts and pages? Follow the{' '}
                <Link
                  to={`/${siteId}/settings/mcp`}
                  className="font-medium underline underline-offset-4"
                >
                  MCP Server setup guide
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        {/* API Keys Table */}
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between gap-3 p-4 border-b">
            <div className="min-w-0 space-y-1">
              <h3 className="font-semibold">Your API Keys</h3>
              {site?.id && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <span>Site ID:</span>
                  <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">
                    {site.id}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(site.id, 'site-id')}
                  >
                    {copiedKeyId === 'site-id' ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              )}
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Key
            </Button>
          </div>

          {apiKeys && apiKeys.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key: ApiKey) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                          {visibleKeyId === key.id ? key.secret : maskSecret(key.secret)}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVisibleKeyId(visibleKeyId === key.id ? null : key.id)}
                        >
                          {visibleKeyId === key.id ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(key.secret, key.id)}
                        >
                          {copiedKeyId === key.id ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={key.access === 'write' ? 'default' : 'secondary'}
                        className="whitespace-nowrap"
                      >
                        {key.access === 'write' ? 'Read & write' : 'Read only'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(key.lastSeenAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(key.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedKeyId(key.id)
                          setIsDeleteDialogOpen(true)
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center">
              <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium mb-1">No API keys yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create an API key to start using the Content API.
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Key
              </Button>
            </div>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog 
          open={isCreateDialogOpen} 
          onOpenChange={(open) => {
            setIsCreateDialogOpen(open)
            if (!open) {
              setNewKeyName('')
              setAllowWrite(false)
              setNewlyCreatedKey(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {newlyCreatedKey ? 'API Key Created' : 'Create API Key'}
              </DialogTitle>
              <DialogDescription>
                {newlyCreatedKey 
                  ? 'Make sure to copy your API key now. You won\'t be able to see it again!'
                  : 'Give your API key a name to help you remember what it\'s used for.'}
              </DialogDescription>
            </DialogHeader>

            {newlyCreatedKey ? (
              <div className="space-y-4 py-4">
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                    ⚠️ This is the only time you'll see this key. Copy it now!
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-white dark:bg-gray-900 px-3 py-2 rounded border font-mono break-all">
                      {newlyCreatedKey}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(newlyCreatedKey, 'new')}
                    >
                      {copiedKeyId === 'new' ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    placeholder="e.g., Production Website, Mobile App"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleCreateKey()
                      }
                    }}
                  />
                </div>
                <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="allowWrite">Allow agents to edit content</Label>
                    <p className="text-xs text-muted-foreground">
                      Lets MCP clients create and edit posts and pages as you. Leave off
                      for read-only Content API access.
                    </p>
                  </div>
                  <Switch
                    id="allowWrite"
                    checked={allowWrite}
                    onCheckedChange={setAllowWrite}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              {newlyCreatedKey ? (
                <Button onClick={() => setIsCreateDialogOpen(false)}>
                  Done
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreateKey} disabled={isCreating}>
                    {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Key
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete API Key</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this API key? Any applications using 
                this key will no longer be able to access your content.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteKey}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeleting}
              >
                {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
